import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Paragraph, Stack, useConfirmationModal, useSnackbars } from '@customerio/pluma-components/react';
import type { Relationship } from '@customerio/hydra-ui-api';
import plur from 'plur';
import { useAnalytics } from '@/services/analytics';
import { getRelatedCustomersQueryOptions, relatedCustomersAllKey } from '@/services/db/object/queries';
import { useDeleteRelationships } from '@/services/db/relationship/mutations';

/** One id for every outcome, so a retry replaces the previous snackbar. */
const SNACKBAR_ID = 'relationships.delete';

type ConfirmDelete = ReturnType<typeof useConfirmationModal>;

function confirmDelete(confirm: ConfirmDelete, count: number): Promise<boolean> {
  const noun = plur('relationship', count);
  return confirm({
    title: `Delete ${noun}`,
    message: (
      <Stack gap="200">
        <Paragraph>
          {count} {noun} will be deleted.
        </Paragraph>
        <Paragraph>Are you sure?</Paragraph>
      </Stack>
    ),
    confirmLabel: 'Yes, delete',
    isCritical: true,
  });
}

export type RelationshipsDeletionOptions = {
  workspaceId: number;
  cioObjectId: string;
  /** Analytics source for the surface the delete was triggered from. */
  source: string;
  /** Every relationship the object has, which is what "delete all" covers. */
  total: number;
  selectedRelationships: Relationship[];
  onDeleted: () => void;
};

export type RelationshipsDeletion = {
  deleteSelected: () => void;
  deleteAll: () => void;
};

/**
 * The relationships table's two destructive actions, each confirmed before it
 * runs. A ref latch rather than pending state: two clicks landing before the
 * re-render would both read a stale flag and open a second confirm.
 */
export function useRelationshipsDeletion(options: RelationshipsDeletionOptions): RelationshipsDeletion {
  const { workspaceId, cioObjectId, source, total, selectedRelationships, onDeleted } = options;
  const queryClient = useQueryClient();
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();
  const { track } = useAnalytics();
  const { mutateAsync } = useDeleteRelationships();
  const isRunning = useRef(false);

  const deleteRows = useCallback(
    async (rows: Relationship[]) => {
      try {
        await mutateAsync(rows);
      } catch {
        snackbars.create({
          id: SNACKBAR_ID,
          variant: 'critical',
          message: 'There was an error deleting relationships. Try again later.',
        });
        return;
      }

      snackbars.create({ id: SNACKBAR_ID, message: `${rows.length} ${plur('relationship', rows.length)} deleted` });
      void track('objects', 'relationshipsManualDeleteCompleted', {
        Source: source,
        'Number of People': selectedRelationships.length,
        'Number of Objects': 1,
      });
      void queryClient.invalidateQueries({ queryKey: relatedCustomersAllKey(workspaceId, cioObjectId) });
      onDeleted();
    },
    [
      cioObjectId,
      mutateAsync,
      onDeleted,
      queryClient,
      selectedRelationships.length,
      snackbars,
      source,
      track,
      workspaceId,
    ],
  );

  const fetchEveryRelationship = useCallback(async (): Promise<Relationship[] | null> => {
    try {
      const data = await queryClient.fetchQuery(
        getRelatedCustomersQueryOptions(workspaceId, cioObjectId, { page: 1, size: total }),
      );
      return data.relationships;
    } catch {
      return null;
    }
  }, [cioObjectId, queryClient, total, workspaceId]);

  const runOnce = useCallback(async (action: () => Promise<void>) => {
    if (isRunning.current) return;
    isRunning.current = true;
    try {
      await action();
    } finally {
      isRunning.current = false;
    }
  }, []);

  const deleteSelected = useCallback(() => {
    void runOnce(async () => {
      if (selectedRelationships.length === 0) return;
      if (!(await confirmDelete(confirm, selectedRelationships.length))) return;
      await deleteRows(selectedRelationships);
    });
  }, [confirm, deleteRows, runOnce, selectedRelationships]);

  const deleteAll = useCallback(() => {
    void runOnce(async () => {
      if (!(await confirmDelete(confirm, total))) return;
      const everyRelationship = await fetchEveryRelationship();
      if (everyRelationship == null) {
        snackbars.create({
          id: SNACKBAR_ID,
          variant: 'critical',
          message: 'There was an error deleting all relationships. Try again later.',
        });
        return;
      }
      await deleteRows(everyRelationship);
    });
  }, [confirm, deleteRows, fetchEveryRelationship, runOnce, snackbars, total]);

  return { deleteSelected, deleteAll };
}
